import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle2, XCircle, User2, Upload, ScanLine, Loader2 } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { displaySerial, isPlaceholderSerial } from "@/lib/serials";
import { formatRam, formatStorage } from "@/lib/specs";
import { useAuth, canFinishQC } from "@/lib/auth";
import { QC } from "@/constants/testIds";

const GRADES = ["A", "B", "C", "D"];
const NA_CODES = new Set(); // reserved for items that can be N/A

function ChecklistRow({ item, entry, onChange, disabled }) {
  const code = item.code;
  const value = entry?.value ?? "";
  const grade = entry?.grade ?? "";
  const result = entry?.result ?? "";

  const setKey = (patch) => onChange(code, { ...entry, ...patch });

  return (
    <tr data-testid={QC.checklistRow(code)} className="border-t border-slate-100 align-top">
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-slate-900">{item.label}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{item.kind}</div>
      </td>
      <td className="px-4 py-3">
        {item.kind === "PASS_FAIL" && (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
            {["PASS", "FAIL", ...(NA_CODES.has(code) ? ["NA"] : [])].map((r) => {
              const key = r === "PASS" ? QC.checklistPass(code) : r === "FAIL" ? QC.checklistFail(code) : QC.checklistNA(code);
              const active = result === r;
              return (
                <button
                  key={r}
                  type="button"
                  disabled={disabled}
                  data-testid={key}
                  onClick={() => setKey({ result: r })}
                  className={`px-3 py-1.5 ${active
                    ? r === "PASS" ? "bg-emerald-600 text-white" : r === "FAIL" ? "bg-rose-600 text-white" : "bg-slate-700 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        )}
        {item.kind === "PERCENT" && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={100}
              disabled={disabled}
              value={value}
              data-testid={QC.checklistValue(code)}
              onChange={(e) => setKey({ value: e.target.value === "" ? undefined : Number(e.target.value) })}
              className="w-24 rounded-md border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
            />
            <span className="text-xs text-slate-500">%</span>
          </div>
        )}
        {item.kind === "GRADE" && (
          <select
            disabled={disabled}
            value={grade}
            data-testid={QC.checklistGrade(code)}
            onChange={(e) => setKey({ grade: e.target.value || undefined })}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          >
            <option value="">—</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        {item.kind === "TEXT" && (
          // V1.9 — Remarks / TEXT items store their answer in `entry.text`.
          <textarea
            disabled={disabled}
            value={entry?.text ?? ""}
            maxLength={2000}
            rows={2}
            data-testid={QC.checklistValue(code)}
            onChange={(e) => setKey({ text: e.target.value || undefined })}
            placeholder="Engineer remarks"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
        )}
      </td>
      <td className="px-4 py-3">
        <input
          type="text"
          disabled={disabled}
          value={entry?.remarks ?? ""}
          maxLength={500}
          data-testid={QC.checklistRemarks(code)}
          onChange={(e) => setKey({ remarks: e.target.value || undefined })}
          placeholder="Notes (optional)"
          className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        />
      </td>
    </tr>
  );
}

export default function QCJobPage() {
  const { passport_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);

  // Form state
  const [checklist, setChecklist] = useState({});
  const [engineerRemarks, setEngineerRemarks] = useState("");
  const [batteryHealth, setBatteryHealth] = useState("");
  const [batteryBackup, setBatteryBackup] = useState("");
  const [ssdHealth, setSsdHealth] = useState("");
  const [cosmeticGrade, setCosmeticGrade] = useState("");
  const [screenGrade, setScreenGrade] = useState("");
  const [actualProcessor, setActualProcessor] = useState("");
  const [actualRam, setActualRam] = useState("");
  const [actualStorage, setActualStorage] = useState("");
  const [actualStorageType, setActualStorageType] = useState("");
  // V2.9 — Item-type-specific QC state (RAM/SSD/Battery/Charger). All optional;
  // only populated when detail.item_type != "laptop". Persisted in the QC
  // record's checklist JSONB — no schema change.
  const [nlRamFrequency, setNlRamFrequency] = useState("");
  // V2.9.1 — RAM Frequency is a dropdown of common speeds + "Other".
  // When "Other" is selected the operator types a custom numeric value.
  const [nlRamFrequencySel, setNlRamFrequencySel] = useState(""); // dropdown selection
  const [nlWinMemTest, setNlWinMemTest] = useState(""); // "YES"/"NO"
  const [nlSsdBadSectors, setNlSsdBadSectors] = useState(""); // "YES"/"NO"
  const [nlBatteryDesignCapWh, setNlBatteryDesignCapWh] = useState("");
  const [nlChargingTest, setNlChargingTest] = useState(""); // "YES"/"NO"
  // V3.0.2 — Editable Serial Number in Edit-QC mode. Seeded from the
  // passport's current value on load; only sent to the server when the
  // user actually changes it.
  const [editSerial, setEditSerial] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // V2.1 — Purchase QC mandatory serial-capture gate.
  //   The Manufacturer Serial Number is the FIRST step of a Purchase QC job.
  //   The backend seeds each passport with a placeholder serial (e.g.
  //   "PENDING-<uuid>") during procurement; the real serial is captured here.
  //   Until a real (non-placeholder) serial is on the passport, the operator
  //   sees ONLY this capture form — no checklist, no config, no Pass/Fail.
  //   We resolve the qc_job_item id (needed by the capture endpoint) by
  //   fetching the consolidated Purchase QC Job and matching passport_id.
  const [qcJobItemId, setQcJobItemId] = useState(null);
  const [serialInput, setSerialInput] = useState("");
  const [capturingSerial, setCapturingSerial] = useState(false);

  const canFinish = canFinishQC(user);
  // V3.0 — QC records can now be edited while the item is still in a
  // QC-adjacent state (AVAILABLE post-PASS, VENDOR_CASE post-FAIL) — the
  // user must click "Edit QC" first, which flips `editMode` on. Everything
  // downstream keys off `editable = !readOnly || editMode`.
  const [editMode, setEditMode] = useState(false);
  const currentStatus = detail?.current_status;
  const isEditableState = currentStatus === "QC_PENDING"
    || currentStatus === "AVAILABLE" || currentStatus === "VENDOR_CASE";
  const readOnly = currentStatus && currentStatus !== "QC_PENDING" && !editMode;
  const needsSerial = !!detail && isPlaceholderSerial(detail.manufacturer_serial);
  // V2.9 — Item-type-aware QC. Laptop keeps the full existing screen; every
  // other item type gets a compact type-specific form.
  const itemType = (detail?.item_type || "laptop").toLowerCase();
  const isLaptop = itemType === "laptop";
  const nonLaptopComplete = useMemo(() => {
    if (isLaptop) return true;
    if (itemType === "ram") {
      const freqOk = nlRamFrequency !== "" && !Number.isNaN(Number(nlRamFrequency));
      const wmtOk = nlWinMemTest === "YES" || nlWinMemTest === "NO";
      return freqOk && wmtOk;
    }
    if (itemType === "ssd") {
      const bsOk = nlSsdBadSectors === "YES" || nlSsdBadSectors === "NO";
      const healthOk = ssdHealth !== "" && !Number.isNaN(Number(ssdHealth));
      return bsOk && healthOk;
    }
    if (itemType === "battery") {
      const hOk = batteryHealth !== "" && !Number.isNaN(Number(batteryHealth));
      const bOk = batteryBackup !== "" && !Number.isNaN(Number(batteryBackup));
      const dOk = nlBatteryDesignCapWh !== "" && !Number.isNaN(Number(nlBatteryDesignCapWh));
      return hOk && bOk && dOk;
    }
    if (itemType === "adapter" || itemType === "charger") {
      return nlChargingTest === "YES" || nlChargingTest === "NO";
    }
    // Fallback for "other" item_type — accept as long as remarks are set.
    return true;
  }, [isLaptop, itemType, nlRamFrequency, nlWinMemTest, nlSsdBadSectors,
      ssdHealth, batteryHealth, batteryBackup, nlBatteryDesignCapWh, nlChargingTest]);

  // V2.0 — Gate the final PASS/FAIL decision behind checklist completion.
  // Rule (matches the backend guard):
  //   • PASS_FAIL items require an explicit "PASS" or "FAIL" result.
  //     Legacy "N/A" or empty is NOT a valid completion.
  //   • PERCENT items require a numeric value.
  //   • GRADE items require a non-empty grade.
  //   • TEXT items are treated as optional (typically free-form remarks).
  // The engineer sees the exact remaining items so they know what to fill.
  const checklistStatus = useMemo(() => {
    const items = template?.items || [];
    const missing = [];
    for (const it of items) {
      const entry = checklist[it.code] || {};
      const kind = it.kind || "PASS_FAIL";
      let ok = false;
      if (kind === "PASS_FAIL") {
        ok = entry.result === "PASS" || entry.result === "FAIL";
      } else if (kind === "PERCENT") {
        const v = entry.value;
        ok = v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));
      } else if (kind === "GRADE") {
        ok = !!(entry.grade || entry.value);
      } else if (kind === "TEXT") {
        ok = true; // TEXT (remarks-style) is optional
      } else {
        // Unknown kind — be conservative and require *any* entry.
        ok = !!(entry.result || entry.value || entry.grade);
      }
      if (!ok) missing.push(it.label || it.code);
    }
    return { complete: missing.length === 0, missing, total: items.length };
  }, [template, checklist]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, t] = await Promise.all([
        api.get(`/qc/jobs/${passport_id}`),
        api.get(`/qc/checklist-template`),
      ]);
      setDetail(d.data);
      setTemplate(t.data);

      // V2.1 — When the manufacturer serial is still a placeholder, resolve
      // the qc_job_item id via the consolidated Purchase QC Job endpoint so
      // the capture form can POST to /qc/jobs/purchase/{po}/items/{item}/serial.
      // No backend change required — the endpoint already returns items
      // keyed by passport_id.
      if (
        d.data?.purchase_row_id &&
        isPlaceholderSerial(d.data?.manufacturer_serial)
      ) {
        try {
          const job = await api.get(`/qc/jobs/purchase/${d.data.purchase_row_id}`);
          const match = (job.data?.items || []).find(
            (it) => String(it.passport_id) === String(passport_id),
          );
          setQcJobItemId(match?.id || null);
        } catch {
          // Non-fatal — the capture form will surface a clear error if the
          // Purchase QC Job isn't reachable for this operator.
          setQcJobItemId(null);
        }
      } else {
        setQcJobItemId(null);
      }

      // Pre-fill from unit info + prior QC if any
      const latest = d.data.latest_qc;
      // V3.0.2 — Seed the editable serial number from the current passport
      // record so it round-trips correctly through the Edit-QC form.
      setEditSerial(d.data.manufacturer_serial || "");
      if (latest?.battery_health_pct != null) setBatteryHealth(String(latest.battery_health_pct));
      if (latest?.battery_backup_min != null) setBatteryBackup(String(latest.battery_backup_min));
      if (latest?.ssd_health_pct != null) setSsdHealth(String(latest.ssd_health_pct));
      if (latest?.actual_processor) setActualProcessor(latest.actual_processor);
      if (latest?.actual_ram_gb != null) setActualRam(String(latest.actual_ram_gb));
      if (latest?.actual_storage_gb != null) setActualStorage(String(latest.actual_storage_gb));
      if (latest?.actual_storage_type) setActualStorageType(latest.actual_storage_type);
      if (latest?.checklist) {
        // V3.0.1 — Filter out backend-managed underscore-prefixed keys
        // (`_edit_history`, `_engineer_remarks`, `_photos`, `_item_type_data`).
        // Those are NOT ChecklistEntry objects (some are arrays / free-form
        // dicts) and re-submitting them would trip the pydantic validator
        // for `checklist: dict[str, ChecklistEntry]` on the /edit endpoint.
        const c = Object.fromEntries(
          Object.entries(latest.checklist).filter(([k]) => !String(k).startsWith("_"))
        );
        setChecklist(c);
        if (latest.checklist._engineer_remarks) setEngineerRemarks(latest.checklist._engineer_remarks);
        if (Array.isArray(latest.checklist._photos)) setPhotos(latest.checklist._photos);
        if (c.COSMETIC_GRADE?.grade) setCosmeticGrade(c.COSMETIC_GRADE.grade);
        if (c.SCREEN_GRADE?.grade) setScreenGrade(c.SCREEN_GRADE.grade);
        // V3.0 — Also restore item-type-specific fields so the QC edit
        // screen shows the previously captured values.
        const itd = latest.checklist._item_type_data || {};
        if (itd.ram_frequency_mhz != null) {
          const freq = String(itd.ram_frequency_mhz);
          setNlRamFrequency(freq);
          const common = ["800","1066","1333","1600","1866","2133","2400","2666","2933","3200","4800"];
          setNlRamFrequencySel(common.includes(freq) ? freq : "OTHER");
        }
        if (itd.ram_windows_memory_test_pass != null)
          setNlWinMemTest(itd.ram_windows_memory_test_pass ? "YES" : "NO");
        if (itd.ssd_bad_sectors != null)
          setNlSsdBadSectors(itd.ssd_bad_sectors ? "YES" : "NO");
        if (itd.battery_design_capacity_wh != null)
          setNlBatteryDesignCapWh(String(itd.battery_design_capacity_wh));
        if (itd.charger_charging_test_pass != null)
          setNlChargingTest(itd.charger_charging_test_pass ? "YES" : "NO");
      } else {
        // Fresh procurement info
        if (d.data.processor) setActualProcessor(d.data.processor);
        if (d.data.ram_gb != null) setActualRam(String(d.data.ram_gb));
        if (d.data.storage_gb != null) setActualStorage(String(d.data.storage_gb));
        if (d.data.storage_type) setActualStorageType(d.data.storage_type);
      }
    } catch (e) {
      setError(errorToMessage(e, "Failed to load QC job"));
    } finally {
      setLoading(false);
    }
  }, [passport_id]);

  useEffect(() => { load(); }, [load]);

  const passportUrl = useMemo(() => `/admin/qc/jobs/${passport_id}`, [passport_id]);

  const onChecklistChange = (code, entry) => {
    setChecklist((prev) => {
      const next = { ...prev };
      const empty = !entry || (entry.result == null && entry.value == null && entry.grade == null && !entry.remarks);
      if (empty) delete next[code];
      else next[code] = entry;
      return next;
    });
  };

  const assign = async (engineerId) => {
    setError(null);
    try {
      const { data } = await api.post(`/qc/jobs/${passport_id}/assign`,
        { engineer_user_id: engineerId });
      setDetail(data);
    } catch (e) {
      setError(errorToMessage(e, "Assignment failed"));
    }
  };

  // V2.1 — Capture the real Manufacturer Serial Number for this passport.
  // Uses the existing endpoint POST /qc/jobs/purchase/{po}/items/{item}/serial.
  // On success we reload the detail; the placeholder gate then falls away
  // and the checklist becomes visible.
  const captureSerial = async () => {
    const sn = serialInput.trim();
    if (!sn) {
      setError("Enter or scan the Manufacturer Serial Number to continue.");
      return;
    }
    if (!detail?.purchase_row_id || !qcJobItemId) {
      setError(
        "Could not resolve this passport's QC job item. Reload the page — " +
          "if the problem persists, open the Purchase QC Job from the Purchase page.",
      );
      return;
    }
    setCapturingSerial(true);
    setError(null);
    try {
      await api.post(
        `/qc/jobs/purchase/${detail.purchase_row_id}/items/${qcJobItemId}/serial`,
        { manufacturer_serial: sn },
      );
      setSerialInput("");
      await load();
    } catch (e) {
      setError(errorToMessage(e, "Could not capture serial number"));
    } finally {
      setCapturingSerial(false);
    }
  };

  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/qc/photos", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((prev) => [...prev, data.url]);
    } catch (e) {
      setError(errorToMessage(e, "Photo upload failed"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const finish = async (decision) => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        decision,
        checklist,
        engineer_remarks: engineerRemarks || undefined,
        battery_health_pct: batteryHealth === "" ? undefined : Number(batteryHealth),
        battery_backup_min: batteryBackup === "" ? undefined : Number(batteryBackup),
        ssd_health_pct: ssdHealth === "" ? undefined : Number(ssdHealth),
        actual_processor: actualProcessor || undefined,
        actual_ram_gb: actualRam === "" ? undefined : Number(actualRam),
        actual_storage_gb: actualStorage === "" ? undefined : Number(actualStorage),
        actual_storage_type: actualStorageType || undefined,
        cosmetic_grade: cosmeticGrade || undefined,
        screen_grade: screenGrade || undefined,
        photos,
        // V2.9 — Item-type-specific QC fields (only meaningful when
        // detail.item_type != 'laptop'; the backend ignores them for laptop
        // QC because Laptop QC never populates these state fields).
        ram_frequency_mhz: nlRamFrequency === "" ? undefined : Number(nlRamFrequency),
        ram_windows_memory_test_pass:
          nlWinMemTest === "YES" ? true : nlWinMemTest === "NO" ? false : undefined,
        ssd_bad_sectors:
          nlSsdBadSectors === "YES" ? true : nlSsdBadSectors === "NO" ? false : undefined,
        battery_design_capacity_wh:
          nlBatteryDesignCapWh === "" ? undefined : Number(nlBatteryDesignCapWh),
        charger_charging_test_pass:
          nlChargingTest === "YES" ? true : nlChargingTest === "NO" ? false : undefined,
        // V3.0.2 — Only send serial when it actually changed AND we're in
        // edit mode. Backend applies uniqueness via existing DB index.
        manufacturer_serial:
          (editMode && editSerial && editSerial.trim() && editSerial.trim() !== (detail?.manufacturer_serial || ""))
            ? editSerial.trim() : undefined,
      };
      // V3.0 — If we're editing a previously-completed QC record, hit the
      // in-place edit endpoint; otherwise the normal finish path.
      const endpoint = editMode
        ? `/qc/jobs/${passport_id}/edit`
        : `/qc/jobs/${passport_id}/finish`;
      const { data } = await api.post(endpoint, body);
      setSuccessBanner({
        decision: data.decision,
        newStatus: data.new_passport_status,
        repairTaskId: data.repair_task_id,
      });
      // V3.0.2 — Exit edit mode BEFORE reloading so the reload's setChecklist /
      // setBatteryHealth / setSerial etc. become the visible source of truth
      // (previously we stayed in editMode and stale local input state
      // continued to render, giving the impression the PASS didn't persist).
      if (editMode) setEditMode(false);
      await load();
    } catch (e) {
      setError(errorToMessage(e, "Finish failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm">Loading QC job…</div>;
  }
  if (!detail) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate("/admin/qc/jobs")} className="text-sm text-slate-600 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to queue
        </button>
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error || "Passport not found"}
        </div>
      </div>
    );
  }

  const items = template?.items || [];
  const alreadyDone = readOnly;

  return (
    <div className="space-y-6" data-testid="qc-job-page">
      <div className="flex items-baseline justify-between">
        <button onClick={() => navigate("/admin/qc/jobs")} className="text-sm text-slate-600 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back to queue
        </button>
        {successBanner && (
          <span
            data-testid={QC.finishStatusBadge}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
              successBanner.decision === "PASS"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"
            }`}
          >
            {successBanner.decision === "PASS" ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            QC {successBanner.decision} · {successBanner.newStatus}
            {successBanner.repairTaskId ? " · Repair task queued" : ""}
          </span>
        )}
      </div>

      {/* Header card */}
      <div data-testid={QC.detailHeader} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">QC Job</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {detail.brand} {detail.model}
            </div>
            <div className="mt-0.5 text-sm text-slate-500">{detail.processor}</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 text-xs">
              {/* V2.1 — Never surface placeholder identifiers ("Awaiting QC")
                  to the operator. Until the real Manufacturer Serial Number
                  is captured, the barcode/serial slots read as "Not captured".
                  The capture form below is the operator's entry point. */}
              <div>
                <span className="text-slate-400">Barcode</span>
                <div className="font-mono text-slate-800">
                  {isPlaceholderSerial(detail.internal_barcode) ? "Not captured" : detail.internal_barcode}
                </div>
              </div>
              <div>
                <span className="text-slate-400">Serial</span>
                <div className="font-mono text-slate-800">
                  {isPlaceholderSerial(detail.manufacturer_serial) ? "Not captured" : detail.manufacturer_serial}
                </div>
              </div>
              <div><span className="text-slate-400">Purchase</span><div className="font-mono text-slate-800">{detail.purchase_id}</div></div>
              <div><span className="text-slate-400">Vendor</span><div className="text-slate-800">{detail.vendor_name || "—"}</div></div>
              <div><span className="text-slate-400">RAM</span><div className="text-slate-800">{formatRam(detail.ram_gb)}</div></div>
              <div><span className="text-slate-400">Storage</span><div className="text-slate-800">{formatStorage(detail.storage_gb, detail.storage_type)}</div></div>
              <div><span className="text-slate-400">Cosmetic (in)</span><div className="text-slate-800">{detail.cosmetic_grade_at_procure || "—"}</div></div>
              <div><span className="text-slate-400">Status</span><div className="text-slate-800">{detail.current_status}</div></div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[220px]">
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <User2 size={12} />
              {detail.assigned_engineer_name || "Unassigned"}
            </div>
            {!alreadyDone && canFinish && (
              <div className="flex gap-2">
                {detail.assigned_engineer_id === user?.id ? (
                  <button
                    type="button"
                    data-testid={QC.unassignBtn}
                    onClick={() => assign(null)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Unassign me
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={QC.assignSelf}
                    onClick={() => assign(user?.id)}
                    className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  >
                    Assign to me
                  </button>
                )}
              </div>
            )}
            {/* V3.0 — Edit QC entry point. Visible whenever the passport is
                still in a QC-adjacent state (AVAILABLE / VENDOR_CASE) and
                the actor has the QC role. Flipping editMode reveals the
                normal QC form pre-filled with prior values; submit hits
                POST /qc/jobs/{id}/edit which updates in place. */}
            {alreadyDone && isEditableState && canFinish && !editMode && (
              <button
                type="button"
                data-testid="qc-edit-btn"
                onClick={() => setEditMode(true)}
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                Edit QC
              </button>
            )}
            {editMode && (
              <div className="flex flex-col items-end gap-1">
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                  EDIT MODE — updates existing record
                </span>
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                >
                  Cancel edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* V3.0.2 — Editable Serial Number in Edit-QC mode. */}
      {editMode && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
             data-testid="qc-edit-serial-block">
          <label className="text-xs font-medium text-slate-700 whitespace-nowrap">Serial Number</label>
          <input
            type="text"
            value={editSerial}
            onChange={(e) => setEditSerial(e.target.value)}
            placeholder="Manufacturer Serial"
            data-testid="qc-edit-serial-input"
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            (was: {detail.manufacturer_serial || "—"})
          </span>
        </div>
      )}

      {/* V2.1 — Mandatory Manufacturer Serial Number capture (Purchase QC).
          This is the FIRST step of a Purchase QC Job. Until a real serial
          is recorded, the checklist and Pass/Fail actions stay hidden. */}
      {needsSerial && !alreadyDone && canFinish && (
        <section
          data-testid="qc-serial-capture"
          className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-5 shadow-sm"
        >
          <div className="text-xs uppercase tracking-widest text-blue-700">
            Step 1 · Manufacturer Serial Number
          </div>
          <div className="mt-1 text-sm text-slate-700">
            Scan or type the real Manufacturer Serial Number printed on this laptop.
            The Laptop Passport Number ({detail.internal_barcode && !isPlaceholderSerial(detail.internal_barcode) ? (
              <span className="font-mono">{detail.internal_barcode}</span>
            ) : (
              <span className="italic text-slate-500">will be generated</span>
            )}) is bound to this serial for its entire lifecycle.
          </div>
          <div className="mt-3 flex items-center gap-2">
            <ScanLine size={16} className="text-slate-400" />
            <input
              autoFocus
              type="text"
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") captureSerial();
              }}
              placeholder="Scan or type Manufacturer Serial Number"
              maxLength={120}
              disabled={capturingSerial}
              data-testid="qc-serial-input"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-blue-600"
            />
            <button
              type="button"
              onClick={captureSerial}
              disabled={capturingSerial || !serialInput.trim() || !qcJobItemId}
              data-testid="qc-serial-save-btn"
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {capturingSerial ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Save Serial &amp; Continue
            </button>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            The inspection checklist unlocks after a valid Manufacturer Serial
            Number is saved. Pass QC remains disabled until every mandatory
            checklist item is completed.
          </div>
        </section>
      )}

      {/* V2.1 — The QC form (config + checklist + photos + Pass/Fail actions)
          only renders once the Manufacturer Serial Number is captured, or
          when the passport is already past QC (read-only view). */}
      {(!needsSerial || alreadyDone) && (
      <>

      {/* V2.9 — Laptop keeps the existing "Actual Configuration" + full
          Checklist screen. Non-laptop item types render a compact,
          item-type-specific QC form below (RAM/SSD/Battery/Charger). */}
      {isLaptop && (<>
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Actual Configuration (post-inspection)</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Processor</label>
            <input
              type="text"
              disabled={alreadyDone}
              value={actualProcessor}
              data-testid={QC.actualProcessor}
              onChange={(e) => setActualProcessor(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">RAM (GB)</label>
            <input
              type="number" min={0}
              disabled={alreadyDone}
              value={actualRam}
              data-testid={QC.actualRam}
              onChange={(e) => setActualRam(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Storage (GB)</label>
            <input
              type="number" min={0}
              disabled={alreadyDone}
              value={actualStorage}
              data-testid={QC.actualStorage}
              onChange={(e) => setActualStorage(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Storage Type</label>
            <input
              type="text"
              disabled={alreadyDone}
              value={actualStorageType}
              data-testid={QC.actualStorageType}
              onChange={(e) => setActualStorageType(e.target.value)}
              placeholder="SSD / NVMe / HDD"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Battery Health %</label>
            <input
              type="number" min={0} max={100}
              disabled={alreadyDone}
              value={batteryHealth}
              data-testid={QC.batteryHealthInput}
              onChange={(e) => setBatteryHealth(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Battery Backup (min)</label>
            <input
              type="number" min={0} max={1440}
              disabled={alreadyDone}
              value={batteryBackup}
              data-testid={QC.batteryBackupInput}
              onChange={(e) => setBatteryBackup(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">SSD Health %</label>
            <input
              type="number" min={0} max={100}
              disabled={alreadyDone}
              value={ssdHealth}
              data-testid={QC.ssdHealthInput}
              onChange={(e) => setSsdHealth(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Cosmetic</label>
              <select
                disabled={alreadyDone}
                value={cosmeticGrade}
                data-testid={QC.cosmeticGradeSelect}
                onChange={(e) => setCosmeticGrade(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">—</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Screen</label>
              <select
                disabled={alreadyDone}
                value={screenGrade}
                data-testid={QC.screenGradeSelect}
                onChange={(e) => setScreenGrade(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">—</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-widest text-slate-400">
          Inspection checklist ({items.length} items)
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 w-1/4">Item</th>
              <th className="text-left px-4 py-2 w-1/4">Result</th>
              <th className="text-left px-4 py-2">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <ChecklistRow
                key={it.code}
                item={it}
                entry={checklist[it.code]}
                onChange={onChecklistChange}
                disabled={alreadyDone}
              />
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* V2.9 — Non-laptop item-type-specific QC form. Renders only when
          detail.item_type is not "laptop". Reuses the shared Remarks +
          Pass/Fail bar below. Reads Capacity/Technology from purchase spec
          (locked/read-only) and captures the small set of QC-relevant
          fields that determine PASS/FAIL. */}
      {!isLaptop && (
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4"
           data-testid={`qc-nonlaptop-${itemType}`}>
        <div className="text-xs uppercase tracking-widest text-slate-400">
          {itemType.toUpperCase()} QC
        </div>

        {itemType === "ram" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Capacity</label>
              <input readOnly disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                     value={detail.ram_spec?.capacity_gb ? `${detail.ram_spec.capacity_gb} GB` : (detail.ram_spec?.capacity_notes || detail.ram_gb ? `${detail.ram_gb} GB` : "—")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Technology</label>
              <input readOnly disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                     value={detail.ram_spec?.technology === "OTHER"
                       ? (detail.ram_spec?.technology_notes || "Other")
                       : (detail.ram_spec?.technology || "—")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Frequency (MHz) *</label>
              {/* V2.9.1 — Dropdown of common DDR speeds + "Other". Selecting
                  "Other" reveals a numeric input for a custom value. */}
              <select
                disabled={alreadyDone}
                value={nlRamFrequencySel}
                data-testid="qc-ram-frequency-select"
                onChange={(e) => {
                  const v = e.target.value;
                  setNlRamFrequencySel(v);
                  if (v === "OTHER") {
                    setNlRamFrequency("");
                  } else {
                    setNlRamFrequency(v);
                  }
                }}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
                <option value="">— Select —</option>
                {["800","1066","1333","1600","1866","2133","2400","2666","2933","3200","4800"].map((v) => (
                  <option key={v} value={v}>{v} MHz</option>
                ))}
                <option value="OTHER">Other</option>
              </select>
              {nlRamFrequencySel === "OTHER" && (
                <input
                  type="number"
                  min={0}
                  max={20000}
                  disabled={alreadyDone}
                  value={nlRamFrequency}
                  data-testid="qc-ram-frequency"
                  onChange={(e) => setNlRamFrequency(e.target.value)}
                  placeholder="Enter frequency in MHz"
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
                />
              )}
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-slate-500 mb-1">Windows Memory Test Passed? *</label>
              <div className="flex gap-3 mt-1">
                {["YES", "NO"].map((v) => (
                  <label key={v} className="inline-flex items-center gap-1 text-sm">
                    <input type="radio" name="ram-wmt" value={v}
                           disabled={alreadyDone}
                           checked={nlWinMemTest === v}
                           data-testid={`qc-ram-wmt-${v.toLowerCase()}`}
                           onChange={() => setNlWinMemTest(v)} />
                    {v}
                  </label>
                ))}
              </div>
              {nlWinMemTest === "NO" && (
                <div className="mt-1 text-xs text-rose-600">
                  Windows Memory Test failed → click <span className="font-medium">Fail QC</span> to route to Vendor Return.
                </div>
              )}
            </div>
          </div>
        )}

        {itemType === "ssd" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Capacity</label>
              <input readOnly disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                     value={detail.storage_gb ? `${detail.storage_gb} GB` : (detail.model || "—")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Technology / Interface</label>
              <input readOnly disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                     value={detail.storage_type || detail.model || "—"} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">SSD Health % *</label>
              <input type="number" min={0} max={100}
                     disabled={alreadyDone}
                     value={ssdHealth}
                     data-testid="qc-ssd-health"
                     onChange={(e) => {
                       // V2.9.1 — Accept only numeric 0..100.
                       const v = e.target.value;
                       if (v === "") { setSsdHealth(""); return; }
                       const n = Number(v);
                       if (!Number.isFinite(n)) return;
                       setSsdHealth(String(Math.max(0, Math.min(100, n))));
                     }}
                     placeholder="0–100"
                     className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-slate-500 mb-1">Bad Sectors? *</label>
              <div className="flex gap-3 mt-1">
                {["YES", "NO"].map((v) => (
                  <label key={v} className="inline-flex items-center gap-1 text-sm">
                    <input type="radio" name="ssd-bs" value={v}
                           disabled={alreadyDone}
                           checked={nlSsdBadSectors === v}
                           data-testid={`qc-ssd-badsectors-${v.toLowerCase()}`}
                           onChange={() => setNlSsdBadSectors(v)} />
                    {v}
                  </label>
                ))}
              </div>
              {nlSsdBadSectors === "YES" && (
                <div className="mt-1 text-xs text-rose-600">
                  Bad sectors detected → click <span className="font-medium">Fail QC</span> to route to Vendor Return.
                </div>
              )}
            </div>
          </div>
        )}

        {itemType === "battery" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Battery Health % *</label>
              <input type="number" min={0} max={100}
                     disabled={alreadyDone}
                     value={batteryHealth}
                     data-testid="qc-battery-health"
                     onChange={(e) => {
                       // V2.9.1 — Accept only numeric 0..100.
                       const v = e.target.value;
                       if (v === "") { setBatteryHealth(""); return; }
                       const n = Number(v);
                       if (!Number.isFinite(n)) return;
                       setBatteryHealth(String(Math.max(0, Math.min(100, n))));
                     }}
                     className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Backup Time (min) *</label>
              <input type="number" min={0} max={1440}
                     disabled={alreadyDone}
                     value={batteryBackup}
                     data-testid="qc-battery-backup"
                     onChange={(e) => {
                       // V2.9.1 — Numeric minutes only (0..1440).
                       const v = e.target.value;
                       if (v === "") { setBatteryBackup(""); return; }
                       const n = Number(v);
                       if (!Number.isFinite(n)) return;
                       setBatteryBackup(String(Math.max(0, Math.min(1440, Math.round(n)))));
                     }}
                     className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Design Capacity (Wh) *</label>
              <input type="number" min={0} max={1000}
                     disabled={alreadyDone}
                     value={nlBatteryDesignCapWh}
                     data-testid="qc-battery-design-cap"
                     onChange={(e) => setNlBatteryDesignCapWh(e.target.value)}
                     className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
            </div>
          </div>
        )}

        {(itemType === "adapter" || itemType === "charger") && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Charging Test *</label>
            <div className="flex gap-3 mt-1">
              {["YES", "NO"].map((v) => (
                <label key={v} className="inline-flex items-center gap-1 text-sm">
                  <input type="radio" name="charger-ct" value={v}
                         disabled={alreadyDone}
                         checked={nlChargingTest === v}
                         data-testid={`qc-charger-test-${v.toLowerCase()}`}
                         onChange={() => setNlChargingTest(v)} />
                  {v}
                </label>
              ))}
            </div>
            {nlChargingTest === "NO" && (
              <div className="mt-1 text-xs text-rose-600">
                Charging test failed → click <span className="font-medium">Fail QC</span> to route to Vendor Return.
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Shared for all item types: Remarks + Photos + Action buttons. */}

      {/* Engineer remarks + photos */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Engineer Remarks</label>
          <textarea
            rows={3}
            disabled={alreadyDone}
            value={engineerRemarks}
            data-testid={QC.engineerRemarks}
            onChange={(e) => setEngineerRemarks(e.target.value)}
            maxLength={2000}
            placeholder="Summary of findings, notable defects, next-step recommendations…"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-2">
              <Camera size={14} /> Photos (optional)
            </label>
            <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              <Upload size={12} /> Upload
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                disabled={alreadyDone || uploadingPhoto}
                data-testid={QC.photoInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadPhoto(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {photos.map((url, i) => (
              <div
                key={url}
                data-testid={QC.photoPreview(i)}
                className="h-20 w-20 rounded-md border border-slate-200 bg-slate-100 overflow-hidden"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                  alt={`QC evidence ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
            {photos.length === 0 && <div className="text-xs text-slate-400">No photos yet.</div>}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {!alreadyDone && canFinish && (
        <div className="sticky bottom-4 rounded-lg border border-slate-200 bg-white p-4 shadow-lg flex items-center justify-between gap-3">
          {/* V2.0 — Checklist-completion helper. Explains why the final
              PASS/FAIL buttons are disabled and lists the remaining items. */}
          <div className="text-xs text-slate-600 min-w-0">
            {!isLaptop ? (
              <span data-testid="qc-nonlaptop-gate" className={nonLaptopComplete ? "text-emerald-700" : "text-rose-700 font-medium"}>
                {nonLaptopComplete
                  ? `Ready — ${itemType.toUpperCase()} QC form complete.`
                  : `Complete required ${itemType.toUpperCase()} QC fields to enable Pass / Fail.`}
              </span>
            ) : checklistStatus.complete ? (
              <span className="text-emerald-700" data-testid="qc-checklist-complete">
                All {checklistStatus.total} checklist items completed.
              </span>
            ) : (
              <span data-testid="qc-checklist-incomplete">
                <span className="font-medium text-rose-700">
                  Complete all checklist items to enable Pass / Fail.
                </span>{" "}
                <span className="text-slate-500">
                  {checklistStatus.missing.length} pending
                  {checklistStatus.missing.length <= 5
                    ? `: ${checklistStatus.missing.join(", ")}`
                    : ""}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              disabled={saving || needsSerial || (isLaptop ? !checklistStatus.complete : !nonLaptopComplete)}
              data-testid={QC.failBtn}
              onClick={() => finish("FAIL")}
              className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <XCircle size={16} /> Fail QC
            </button>
            <button
              type="button"
              disabled={saving || needsSerial || (isLaptop ? !checklistStatus.complete : !nonLaptopComplete)}
              data-testid={QC.passBtn}
              onClick={() => finish("PASS")}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <CheckCircle2 size={16} /> Pass QC
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {alreadyDone && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          This passport is no longer in QC_PENDING (current: <span className="font-mono">{detail.current_status}</span>).
          The form above is read-only.
        </div>
      )}

      {/* V2.2 — QC Decision Reversal.
          Visible only to Reception / Admin / Super Admin, and only when the
          passport is exactly at AVAILABLE (currently "Passed") or
          VENDOR_CASE (currently "Failed"). Any downstream state (RESERVED,
          PACKED, SHIPPED, SOLD, …) locks the reversal because those steps
          must be undone through their own return / repair paths. */}
      {detail && ["SUPER_ADMIN", "ADMIN", "RECEPTION"].includes(user?.role)
        && ["AVAILABLE", "VENDOR_CASE"].includes(detail.current_status) && (
        <QCReversalCard
          detail={detail}
          onReversed={load}
        />
      )}
    </div>
  );
}

// Preset correction reasons. `code` is stored (prefixed in the reason
// string) so future analytics can group / search by category via a simple
// LIKE query without a schema change.
const QC_REVERSAL_PRESETS = [
  { code: "wrong_button",     label: "Wrong button clicked" },
  { code: "wrong_unit",       label: "Wrong unit selected" },
  { code: "spec_mismatch",    label: "Spec mismatch corrected" },
  { code: "retest_different", label: "Re-test revealed different result" },
  { code: "data_entry_error", label: "Data entry error" },
  { code: "other",            label: "Other (specify below)" },
];

function QCReversalCard({ detail, onReversed }) {
  const [open, setOpen] = useState(false);
  const [presetCode, setPresetCode] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isCurrentlyPassed = detail.current_status === "AVAILABLE";
  const label = isCurrentlyPassed ? "Mark as Failed" : "Mark as Passed";
  const nextLabel = isCurrentlyPassed ? "Failed" : "Passed";
  const currentLabel = isCurrentlyPassed ? "Passed" : "Failed";

  const buildReason = () => {
    const trimmedNotes = notes.trim();
    if (!presetCode) return trimmedNotes || null;
    const preset = QC_REVERSAL_PRESETS.find((p) => p.code === presetCode);
    if (!preset) return trimmedNotes || null;
    // Prefix keeps the reason greppable: `[wrong_button] Wrong button clicked — free text`
    const base = `[${preset.code}] ${preset.label}`;
    return trimmedNotes ? `${base} — ${trimmedNotes}` : base;
  };

  const canSubmit = () => {
    // Require either a preset OR free-text; if "Other" is picked, require free-text.
    if (presetCode === "other") return notes.trim().length > 0;
    if (!presetCode) return notes.trim().length > 0;
    return true;
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/qc/passports/${detail.passport_id}/reverse-decision`,
        { reason: buildReason() });
      setOpen(false); setPresetCode(""); setNotes("");
      await onReversed();
    } catch (e) {
      setError(errorToMessage(e, "Could not reverse decision"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border-2 border-amber-200 bg-amber-50/40 p-4"
             data-testid="qc-reversal-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-700">
            QC Decision Correction
          </div>
          <div className="mt-1 text-sm text-slate-700">
            Current QC result: <span className="font-medium">{currentLabel}</span>.
            Use this only to fix an accidental wrong selection.
          </div>
        </div>
        <button type="button" onClick={() => setOpen(true)}
                data-testid="qc-reverse-open-btn"
                className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700">
          {label}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
             data-testid="qc-reverse-modal">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold text-slate-900">
              Change QC result to <span className="text-amber-700">{nextLabel}</span>?
            </div>
            <div className="mt-2 text-sm text-slate-600">
              You are about to change the QC result. This will update inventory,
              workflow status and purchase statistics.
              <span className="block mt-1">Do you want to continue?</span>
            </div>
            <label className="mt-3 block text-xs font-medium text-slate-700">
              Correction reason
              <select
                value={presetCode}
                onChange={(e) => setPresetCode(e.target.value)}
                data-testid="qc-reverse-reason-preset"
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-600">
                <option value="">-- Select a reason --</option>
                {QC_REVERSAL_PRESETS.map((p) => (
                  <option key={p.code} value={p.code}>{p.label}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-xs font-medium text-slate-700">
              Additional notes {presetCode === "other" ? "(required)" : "(optional)"}
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={400} rows={2}
                placeholder={presetCode === "other"
                  ? "Describe the reason for reversing this QC decision"
                  : "e.g., Additional context for this correction"}
                data-testid="qc-reverse-reason"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-600"
              />
            </label>
            {error && (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                      data-testid="qc-reverse-cancel-btn"
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy || !canSubmit()}
                      data-testid="qc-reverse-confirm-btn"
                      className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60">
                {busy ? "Working..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
